import type jsPDF from "jspdf";
import {
    applyBrandedPageFooter, createPdfDocument, drawPdfPill, drawPdfSectionTitle, drawPdfSubsectionLabel, ensurePdfSpace, loadPdfFonts,
    PDF_FOOTER_RESERVE, PDF_MARGIN_X, pdfColors, pdfDate, pdfDrawable, PdfTone, renderPdfTable,
} from "../exportToPdfHelper";
import { saveBlob } from "../request";
import { LINK_LABEL, Research, ResearchMessage, ResearchRange, ResearchSource, ResearchTable, SOURCE_LABEL } from "../../types/bd/Research";

/**
 * Downloads from Research: a table as Excel, a chart or diagram as PNG,
 * and the whole page as one PDF in the CRX style. Pictures are drawn with the
 * light colours whatever the screen theme, because they go into documents.
 */

/** A safe file name: no characters Windows or macOS refuse. */
export const fileSafe = (name: string): string => name.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 80) || "research";

/**
 * Which columns show plain numbers (no thousands separator): years and the
 * like — a column headed "Year", "ID", "No." or "Reference", or whose numbers
 * are all whole numbers from 1900 to 2100.
 */
export const plainColumns = (t: ResearchTable): boolean[] => t.columns.map((c, i) => {
    if (/\b(year|yr|fy|id|ref|reference|code|no\.?|number|line)\b/i.test(c)) return true;
    const nums = t.rows.map((r) => r[i]).filter((v): v is number => typeof v === "number");
    return nums.length > 0 && nums.every((v) => Number.isInteger(v) && v >= 1900 && v <= 2100);
});

/** A cell as text: numbers grouped ("1,200,000") unless the column is plain ("2024"). */
export const cellText = (v: string | number | null | undefined, plain = false): string =>
    v === null || v === undefined ? "" : typeof v === "number" ? (plain ? String(v) : v.toLocaleString("en-US", { maximumFractionDigits: 2 })) : String(v);

/** A range check's figures in one line, as on the page and in the PDF. */
export const rangeSummary = (r: ResearchRange): string => {
    const vsMedian = r.median ? Math.round(((r.value - r.median) / Math.abs(r.median)) * 100) : 0;
    const median = vsMedian === 0 ? "level with the median" : `${Math.abs(vsMedian)}% ${vsMedian > 0 ? "above" : "below"} the median`;
    return `Lowest ${cellText(r.min)} · lower quartile ${cellText(r.q1)} · median ${cellText(r.median)} · upper quartile ${cellText(r.q3)} · highest ${cellText(r.max)}. `
        + `${r.label} (${cellText(r.value)}) is ${median}.${r.skipped ? ` ${r.skipped} row${r.skipped === 1 ? "" : "s"} had no single number and ${r.skipped === 1 ? "was" : "were"} left out.` : ""}`;
};

/** The table as tab-separated lines: pastes into Excel, Sheets or an email as a table. */
export const tableToTsv = (t: ResearchTable): string => {
    const plain = plainColumns(t);
    return [t.columns, ...t.rows].map((r) => r.map((c, i) => cellText(c, plain[i]).replace(/[\t\n]/g, " ")).join("\t")).join("\n");
};

/** Download a table as an Excel file: gold header row, frozen, numbers kept as numbers. */
export async function downloadTableXlsx(t: ResearchTable): Promise<void> {
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = "CRX";
    wb.created = new Date();
    const ws = wb.addWorksheet(fileSafe(t.title).replace(/[[\]]/g, " ").slice(0, 31) || "Table");
    ws.addRow(t.columns);
    t.rows.forEach((r) => ws.addRow(t.columns.map((_c, i) => r[i] ?? "")));
    const head = ws.getRow(1);
    head.font = { bold: true, color: { argb: "FF1F2023" } };
    head.eachCell((cell) => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E021" } }; });
    const plain = plainColumns(t);
    t.columns.forEach((c, i) => {
        const longest = Math.max(c.length, ...t.rows.map((r) => cellText(r[i], plain[i]).length));
        ws.getColumn(i + 1).width = Math.min(50, Math.max(9, longest + 3));
        if (!plain[i] && t.rows.length && t.rows.every((r) => typeof r[i] === "number" || r[i] === "")) ws.getColumn(i + 1).numFmt = "#,##0.##";
    });
    ws.views = [{ state: "frozen", ySplit: 1 }];
    const buffer = await wb.xlsx.writeBuffer();
    saveBlob(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${fileSafe(t.title)}.xlsx`);
}

const STYLE_PROPS = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-linejoin", "paint-order", "font-size", "font-family", "font-weight", "opacity"];

/**
 * Draw an on-screen SVG to a canvas with the light colours (the dark theme is
 * switched off for the moment the styles are read, then put back).
 * @param svgId - The figure's SVG id.
 * @returns The canvas and its size in CSS pixels, or null when it can't be drawn.
 */
export async function rasterizeFigure(svgId: string): Promise<{ canvas: HTMLCanvasElement; width: number; height: number } | null> {
    const svg = document.getElementById(svgId) as unknown as SVGSVGElement | null;
    if (!svg?.viewBox?.baseVal) return null;
    const wasDark = document.body.classList.contains("dark");
    let xml = "";
    let background = "#FFFFFF";
    const box = svg.viewBox.baseVal;
    if (wasDark) document.body.classList.remove("dark");
    try {
        const clone = svg.cloneNode(true) as SVGSVGElement;
        const src = [svg, ...Array.from(svg.querySelectorAll("*"))];
        const dst = [clone, ...Array.from(clone.querySelectorAll("*"))];
        src.forEach((el, i) => {
            const cs = getComputedStyle(el);
            (dst[i] as Element).setAttribute("style", STYLE_PROPS.map((p) => `${p}:${cs.getPropertyValue(p)}`).join(";"));
        });
        clone.setAttribute("width", String(box.width));
        clone.setAttribute("height", String(box.height));
        background = getComputedStyle(document.body).getPropertyValue("--surface-card").trim() || background;
        xml = new XMLSerializer().serializeToString(clone);
    } finally {
        if (wasDark) document.body.classList.add("dark");
    }
    const img = new Image();
    const loaded = await new Promise<boolean>((resolve) => {
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
    });
    if (!loaded) return null;
    const pad = 24;
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = (box.width + pad * 2) * scale;
    canvas.height = (box.height + pad * 2) * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, pad * scale, pad * scale, box.width * scale, box.height * scale);
    return { canvas, width: box.width + pad * 2, height: box.height + pad * 2 };
}

/** Download a chart or diagram as a PNG. */
export async function downloadFigurePng(svgId: string, title: string): Promise<boolean> {
    const drawn = await rasterizeFigure(svgId);
    if (!drawn) return false;
    const blob = await new Promise<Blob | null>((resolve) => drawn.canvas.toBlob(resolve, "image/png"));
    if (!blob) return false;
    saveBlob(blob, `${fileSafe(title)}.png`);
    return true;
}

const SOURCE_TONE: Record<ResearchSource, PdfTone> = { files: "neutral", crx: "success", web: "warning", general: "info" };
const BODY_SIZE = 9.2;

/** Wrapped body text from y; returns the y below it. */
const paragraph = (doc: jsPDF, y: number, text: string, opts: { size?: number; color?: [number, number, number]; bold?: boolean; indent?: number } = {}): number => {
    const size = opts.size ?? BODY_SIZE;
    const line = size * 0.3528 * 1.45;
    const width = doc.internal.pageSize.getWidth() - PDF_MARGIN_X * 2 - (opts.indent ?? 0);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(size);
    const lines: string[] = doc.splitTextToSize(pdfDrawable(text), width);
    let at = y;
    lines.forEach((l) => {
        at = ensurePdfSpace(doc, at, line);
        doc.setFont("helvetica", opts.bold ? "bold" : "normal");
        doc.setFontSize(size);
        doc.setTextColor(...(opts.color ?? pdfColors.body));
        doc.text(l, PDF_MARGIN_X + (opts.indent ?? 0), at + line * 0.75);
        at += line;
    });
    return at + 1.5;
};

/** A table in the shared PDF style; returns the y below it. */
const pdfTable = (doc: jsPDF, y: number, t: ResearchTable, isEdited = false): number => {
    let at = ensurePdfSpace(doc, y, 18);
    at = paragraph(doc, at, `${t.title}${isEdited ? " (edited by hand)" : ""}`, { bold: true, size: 9, color: pdfColors.strong });
    const plain = plainColumns(t);
    const end = renderPdfTable(doc, { columns: t.columns.map((c) => ({ header: pdfDrawable(c) })), body: t.rows.map((r) => t.columns.map((_c, i) => pdfDrawable(cellText(r[i], plain[i])))), startY: at });
    return end + 5;
};

/** A drawn picture, as wide as the page allows; returns the y below it. */
const pdfFigure = async (doc: jsPDF, y: number, svgId: string): Promise<number> => {
    const drawn = await rasterizeFigure(svgId);
    if (!drawn) return y;
    const contentW = doc.internal.pageSize.getWidth() - PDF_MARGIN_X * 2;
    const maxH = doc.internal.pageSize.getHeight() - PDF_FOOTER_RESERVE - 30;
    let w = Math.min(contentW, drawn.width * 0.26);
    let h = w * (drawn.height / drawn.width);
    if (h > maxH) { h = maxH; w = h * (drawn.width / drawn.height); }
    const at = ensurePdfSpace(doc, y, h + 4);
    doc.addImage(drawn.canvas.toDataURL("image/png"), "PNG", PDF_MARGIN_X, at, w, h);
    return at + h + 5;
};

/** One answer's labelled parts, table and pictures; returns the y below it. */
const pdfAnswer = async (doc: jsPDF, y: number, m: ResearchMessage, svgIdFor: (m: ResearchMessage, which: "chart" | "diagram" | "range") => string): Promise<number> => {
    let at = ensurePdfSpace(doc, y, 16);
    at = paragraph(doc, at, m.question, { bold: true, size: 10, color: pdfColors.strong });
    for (const part of m.answer.parts) {
        at = ensurePdfSpace(doc, at, 12);
        drawPdfPill(doc, { x: PDF_MARGIN_X, y: at, text: pdfDrawable(SOURCE_LABEL[part.from].label).toUpperCase(), tone: SOURCE_TONE[part.from], fontSize: 6.5 });
        at = paragraph(doc, at + 6.5, part.text);
    }
    if (m.answer.table) at = pdfTable(doc, at, m.answer.table, m.answer.tableEdited);
    if (m.answer.range) {
        at = await pdfFigure(doc, at, svgIdFor(m, "range"));
        at = paragraph(doc, at, `Range check (worked out by CRX, not the AI, the same way as Excel's QUARTILE): ${rangeSummary(m.answer.range)}`, { size: 8.5 });
    }
    if (m.answer.chart) at = await pdfFigure(doc, at, svgIdFor(m, "chart"));
    if (m.answer.diagram) at = await pdfFigure(doc, at, svgIdFor(m, "diagram"));
    if (m.masked) at = paragraph(doc, at, m.masked, { size: 7.5, color: pdfColors.muted });
    return at + 4;
};

/**
 * Download the whole page as one PDF: its link, files, the company profile
 * (company pages) and every question with its labelled answer, tables and
 * pictures (taken from the page as drawn, so they must be on screen).
 */
export async function downloadResearchPdf(r: Research, opts: {
    svgIdFor: (m: ResearchMessage, which: "chart" | "diagram" | "range") => string;
    profileOrgSvgId?: string;
}): Promise<void> {
    await loadPdfFonts();
    const fileName = `${fileSafe(r.title)}.pdf`;
    const linkLine = r.link ? `${LINK_LABEL[r.link.type]}${r.link.reference ? ` ${r.link.reference}` : ""} · ${r.link.name}` : "Not linked to a CRX record";
    const { doc, yPos } = createPdfDocument({
        fileName, orientation: "portrait",
        header: { title: pdfDrawable(r.title), eyebrow: r.kind === "company" ? "Ask CRX page · Company" : "Ask CRX page", subtitle: pdfDrawable(linkLine) },
        runningHeader: { title: pdfDrawable(r.title) },
    });
    let y = paragraph(doc, yPos, "Names, emails, phone numbers and amounts were hidden from the AI. Parts marked “AI's own knowledge” are not from the files or CRX: check them before you rely on them.", { size: 8, color: pdfColors.muted });

    const files = r.files ?? [];
    if (files.length) {
        y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: "Files on this page", count: files.length });
        y = renderPdfTable(doc, {
            columns: [{ header: "File or note", width: 3 }, { header: "What CRX read", width: 2 }, { header: "Added", width: 2 }],
            body: files.map((f) => [pdfDrawable(f.name), pdfDrawable(f.parts ?? ""), pdfDrawable(`${f.createdByName ?? ""}${f.createdAt ? ` · ${pdfDate(f.createdAt)}` : ""}`)]),
            startY: y,
        }) + 6;
    }

    const p = r.profile;
    if (r.kind === "company" && p) {
        y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y, 24), { title: "Profile" });
        if (p.overview) y = paragraph(doc, y, p.overview);
        if (p.numbers.length) y = pdfTable(doc, y, { title: "Key figures", columns: ["Figure", "Value"], rows: p.numbers.map((n) => [n.label, n.value]) });
        if (p.known) {
            y = ensurePdfSpace(doc, y, 12);
            drawPdfPill(doc, { x: PDF_MARGIN_X, y, text: pdfDrawable(SOURCE_LABEL.general.label).toUpperCase(), tone: "info", fontSize: 6.5 });
            y = paragraph(doc, y + 6.5, p.known);
        }
        if (opts.profileOrgSvgId && p.people.length) y = await pdfFigure(doc, y, opts.profileOrgSvgId);
        if (p.fit.length) y = pdfTable(doc, y, { title: "Fit with our services", columns: ["Service", "Fit", "Why"], rows: p.fit.map((f) => [f.service, f.match, f.why]) });
        if (p.risks.length) { y = drawPdfSubsectionLabel(doc, ensurePdfSpace(doc, y, 14), "Worth knowing"); p.risks.forEach((x) => { y = paragraph(doc, y, `•  ${x}`, { indent: 2 }); }); }
        if (p.questions.length) { y = drawPdfSubsectionLabel(doc, ensurePdfSpace(doc, y, 14), "Questions for the first meeting"); p.questions.forEach((x, i) => { y = paragraph(doc, y, `${i + 1}.  ${x}`, { indent: 2 }); }); }
    }

    const messages = r.messages ?? [];
    if (messages.length) {
        y = drawPdfSectionTitle(doc, ensurePdfSpace(doc, y + 2, 24), { title: "Questions and results", count: messages.length });
        for (const m of messages) y = await pdfAnswer(doc, y, m, opts.svgIdFor);
    }

    applyBrandedPageFooter(doc, { centerText: "Ask CRX pages · Internal" });
    saveBlob(doc.output("blob"), fileName);
}
